import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import MorningTide from './MorningTide';
import Footer from './Footer';
import mananImg from '../images/manan.png';
import ayushImg from '../images/ayush.jpg';
import aarushImg from '../images/aarush.jpg';
import mokshImg from '../images/moksh.webp';
import ekaagarImg from '../images/ekaagar.jpeg';
import './Page.css';
import './Community.css';

const JOIN_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdT2f0vWvu_LsvD23dL3occy3AOV4Ejp4NjqNsResyRBXNE2w/viewform';
const PARTNER_EMAIL = 'earmeoutnonprofit@gmail.com';
const PARTNER_GMAIL = `https://mail.google.com/mail/?view=cm&fs=1&to=${PARTNER_EMAIL}&su=${encodeURIComponent('Partnership Inquiry — EarMeOut')}&body=${encodeURIComponent("Hi EarMeOut team,\n\nWe'd love to explore partnering with you.\n\n")}`;

const TEAM = [
  { name: 'Manan Singh', role: 'Co-Founder & CEO', image: mananImg },
  { name: 'Aarush Channur', role: 'Co-Founder & COO', image: aarushImg },
  { name: 'Ekaagar Chawla', role: 'Lead Developer & Marketing Manager', image: ekaagarImg },
  { name: 'Moksh Khemka', role: 'Lead Developer', image: mokshImg },
  { name: 'Ayush Gupta', role: 'Social Media Manager', image: ayushImg },
];

/**
 * Pointer-tracking 3D tilt — same interaction model as the Crisis help cards.
 * Drives CSS custom properties via a smoothed rAF loop; skipped on touch.
 */
const useTilt = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(hover: none)').matches) return;

    let raf = null;
    let cx = 50, cy = 50, tx = 50, ty = 50;

    const apply = () => {
      el.style.setProperty('--pointer-x', `${cx}%`);
      el.style.setProperty('--pointer-y', `${cy}%`);
      el.style.setProperty('--rotate-x', `${((cy - 50) / 7).toFixed(2)}deg`);
      el.style.setProperty('--rotate-y', `${(-(cx - 50) / 7).toFixed(2)}deg`);
    };

    const loop = () => {
      cx += (tx - cx) * 0.15;
      cy += (ty - cy) * 0.15;
      apply();
      if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
      }
    };

    const start = () => { if (!raf) raf = requestAnimationFrame(loop); };
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      start();
    };
    const onEnter = () => el.classList.add('tilt-active');
    const onLeave = () => { tx = 50; ty = 50; el.classList.remove('tilt-active'); start(); };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);
};

const TeamCard = ({ member }) => {
  const ref = useRef(null);
  useTilt(ref);
  const initials = member.name.split(' ').map(part => part[0]).join('');
  return (
    <div className="community-person-outer">
      <article className="community-person" ref={ref}>
        <div className="community-person-glow" aria-hidden="true" />
        <div className="community-person-image">
          {member.image ? <img src={member.image} alt={member.name} /> : <span>{initials}</span>}
        </div>
        <div className="community-person-meta"><h3>{member.name}</h3><p>{member.role}</p></div>
      </article>
    </div>
  );
};

const PARTNER_POINTS = [
  { title: 'Schools & campuses', copy: 'Peer-support workshops and awareness programming.' },
  { title: 'Nonprofits & NGOs', copy: 'Shared missions, wider reach, real outcomes.' },
  { title: 'Wellness & clinical', copy: 'Vetted resources routed to people who need them.' },
];

const PartnerInvite = () => {
  const ref = useRef(null);
  useTilt(ref);

  return (
    <div className="partner-outer">
      <div className="partner-invite" ref={ref}>
        <div className="partner-aurora" aria-hidden="true"><i /><i /><i /></div>
        <div className="partner-spotlight" aria-hidden="true" />

        <div className="partner-invite-inner">
          <div className="partner-left">
            <div className="partner-brandline">Partner with<br /><strong>EarMeOut</strong></div>
            <ul className="partner-points">
              {PARTNER_POINTS.map(p => (
                <li key={p.title}>
                  <span className="partner-point-dot" aria-hidden="true" />
                  <div><strong>{p.title}</strong><span>{p.copy}</span></div>
                </li>
              ))}
            </ul>
          </div>

          <div className="partner-copy">
            <span className="partner-eyebrow"><span className="partner-eyebrow-dot" aria-hidden="true" />Our network is growing</span>
            <h3>Let&rsquo;s build something<br /><em>meaningful together.</em></h3>
            <p>We partner with schools, nonprofits, wellness groups, and mission-aligned organizations to bring stigma-free mental-health support to more communities.</p>
            <div className="partner-actions">
              <a className="partner-cta" href={PARTNER_GMAIL} target="_blank" rel="noopener noreferrer">
                <span>Partner with us</span>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M8 7h9v9" /></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Community = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    window.setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [location.hash]);

  return (
    <div className="page community-page">
      <MorningTide />
      <Navigation />
      <main className="page-content community-content">
        <span className="community-kicker">people make the mission</span>
        <h1 className="page-title community-title">Our Team</h1>
        <p className="community-intro">Meet the team, build with us, or partner with EarMeOut to make mental-health support more accessible.</p>

        <section id="team" className="community-section community-section--team">
          <div className="community-section-heading"><span>01</span><div><h2>Meet the team</h2><p>Students building a kinder, more accessible space to talk.</p></div></div>
          <div className="community-team-grid">
            {TEAM.map(member => <TeamCard member={member} key={member.name} />)}
          </div>
        </section>

        <section id="partners" className="community-section partner-section">
          <div className="community-section-heading"><span>02</span><div><h2>Partner with us</h2><p>Bring workshops, technology, and stigma-free support to more communities.</p></div></div>
          <PartnerInvite />
        </section>

        <section id="join" className="community-section join-section">
          <div className="community-section-heading"><span>03</span><div><h2>Join EarMeOut</h2><p>Turn your skills and ideas into real mental-health impact.</p></div></div>
          <div className="join-trailer">
            <div className="join-trailer-preview" aria-hidden="true">
              <iframe
                src={`${JOIN_FORM_URL}?embedded=true`}
                title="Preview of the EarMeOut interest form"
                className="join-trailer-frame"
                tabIndex={-1}
                scrolling="no"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
              >Loading form…</iframe>
              <div className="join-trailer-fade" />
            </div>
            <div className="join-trailer-cta">
              <span className="join-trailer-badge"><span className="join-trailer-badge-dot" />Takes about 2 minutes</span>
              <h3>Ready to make an impact?</h3>
              <p>A few quick questions about you and how you&rsquo;d like to help — no commitment, just a hello.</p>
              <a className="join-cta" href={JOIN_FORM_URL} target="_blank" rel="noopener noreferrer">
                <span>Open the full form</span>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M8 7h9v9" /></svg>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Community;
